import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { User, UserCheck, UserPlus, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";

interface ProfileHeaderProps {
  profile: {
    username: string;
    avatar_url: string | null;
    privacy_setting: string;
    country_code?: string;
    followers_count: number;
    following_count: number;
  };
  isFollowing: boolean;
  isOwnProfile: boolean;
  onFollowAction: () => void;
  followMutationPending: boolean;
  unfollowMutationPending: boolean;
}

const ProfileHeader = ({
  profile,
  isFollowing,
  isOwnProfile,
  onFollowAction,
  followMutationPending,
  unfollowMutationPending
}: ProfileHeaderProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center gap-4 mb-6">
      <div className="flex-shrink-0">
        <Avatar className="w-20 h-20">
          <AvatarImage src={profile.avatar_url || undefined} alt={profile.username} />
          <AvatarFallback>
            {profile.username?.[0]?.toUpperCase() || <User />}
          </AvatarFallback>
        </Avatar>
      </div>
      <div className="flex-grow">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold">{profile.username}</h1>
          {profile.privacy_setting !== 'public' && (
            <Lock className="w-4 h-4 text-muted-foreground" />
          )}
        </div>
        {profile.country_code && (
          <p className="text-sm text-gray-600">{profile.country_code}</p>
        )}
        <div className="flex gap-4 text-sm text-gray-600">
          <span>{profile.followers_count || 0} followers</span>
          {profile.privacy_setting === 'public' && (
            <span>{profile.following_count || 0} following</span>
          )}
        </div>
        {!isOwnProfile && (
          <Button
            onClick={onFollowAction}
            disabled={followMutationPending || unfollowMutationPending}
            className="mt-2"
            variant={isFollowing ? "default" : "outline"}
          >
            {isFollowing ? (
              <>
                <UserCheck className="w-4 h-4 mr-2" />
                {t('profile.following')}
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4 mr-2" />
                {t('profile.follow')}
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ProfileHeader;